import {
  FluidButtonConfig,
  FluidContextMenuLink,
  FluidFormElementConfig,
  FluidFormElementState,
  FluidIconKey,
  FluidPopoverConfig,
  FluidSearchSelectOptions,
  FluidTheme,
} from '@lmig/fluid-core';
import { h } from '@stencil/core';

import { FocusIndicator } from '../../../common/fluid-common-form.components';
import { InfoPopover } from '../../../common/fluid-common-icon.components';
import { FluidContextMenuCustomEvent } from '../../../components';
import { ENTER, ESCAPE, EventUtils } from '../../../utils/events/event-utils';
import { hasValue } from '../../../utils/forms/form-method.utils';

export interface FluidSearchBoxTranslationOptions {
  searchBox?: Pick<FluidFormElementConfig, 'placeholder' | 'helpText'>;
  searchButton?: Pick<FluidButtonConfig, 'label' | 'popoverConfig'>;
}

export interface SearchBoxProps {
  searchConfig: FluidFormElementConfig;
  searchTerm: string;
  onSearch: () => void;
  onSearchBoxValueChange: (
    state: FluidFormElementState,
    selectRef: HTMLFluidSelectElement
  ) => void;
  helpText?: string | FluidPopoverConfig;
  identifier?: string;
  searchBoxType?: string;
  selectConfig?: FluidSearchSelectOptions;
  hasFocus?: boolean;
  deleteDebounceTime?: number;
  enableSearchHistory?: boolean;
  recentSearches?: any;
}

/**
 * SearchBox - embedded Input Field
 */
export const SearchBox = (props: SearchBoxProps) => {
  const {
    searchConfig,
    searchTerm,
    helpText,
    onSearch,
    identifier,
    searchBoxType,
    selectConfig,
    hasFocus,
    deleteDebounceTime,
    onSearchBoxValueChange,
    enableSearchHistory,
    recentSearches,
  } = props;
  let inputRef: HTMLFluidInputFieldElement;
  let selectRef: HTMLFluidSelectElement;
  let contextMenuRef: HTMLFluidContextMenuElement;

  const focusIndicatorHost = {
    host: { tagName: 'fluid-search-box' },
    disabled: false,
    readonly: false,
  };

  const setInputRef = async (el: HTMLFluidInputFieldElement) => {
    {
      inputRef = el;
      if (searchTerm === '' && el?.reset) {
        await el?.reset();
      }
    }
  };

  const handleKeypress = (keypress: KeyboardEvent) => {
    if (EventUtils.isKey(keypress, [ENTER])) {
      onSearch();
      contextMenuRef?.toggleMenu(false);
    }
    if (EventUtils.isKey(keypress, [ESCAPE]) && inputRef) {
      inputRef.reset(true).then(() => {
        onSearchBoxValueChange({ value: '', cleared: true }, selectRef);
        onSearch();
      });
    }
  };

  const shouldRenderContextMenu =
    enableSearchHistory && recentSearches?.[0]?.links?.length > 0;

  const handleLinkClick = (
    event: FluidContextMenuCustomEvent<FluidContextMenuLink>
  ) => {
    const link = event.detail;
    inputRef?.setValue(link.label);
    onSearch();
  };

  return (
    <div
      class={{
        [`${searchBoxType}`]: true,
        'search-container': true,
        'with-criteria': hasValue(selectConfig),
      }}
      data-test-id={`${identifier}-search-container`}>
      {hasValue(selectConfig) && (
        <fluid-select
          ref={(el) => (selectRef = el)}
          _enableParts={true}
          _toggleIconKey={FluidIconKey.CHEVRON_DOWN}
          config={{
            ...selectConfig,
            inlineField: true,
            allowCancel: false,
          }}
        />
      )}
      {shouldRenderContextMenu ? (
        <fluid-context-menu
          class={{ 'search-box-dropdown': true }}
          asWrapper={true}
          ref={(el) => (contextMenuRef = el)}
          menuSections={recentSearches}
          data-test-id={`${identifier}-context-menu`}
          disableFocusTransfer={true}
          block={true}
          onLinkClicked={handleLinkClick}>
          <SearchField
            searchConfig={searchConfig}
            searchTerm={searchTerm}
            deleteDebounceTime={deleteDebounceTime}
            setInputRef={setInputRef}
            handleKeypress={handleKeypress}
            onSearchBoxValueChange={onSearchBoxValueChange}
            identifier={identifier}
          />
        </fluid-context-menu>
      ) : (
        <SearchField
          searchConfig={searchConfig}
          searchTerm={searchTerm}
          deleteDebounceTime={deleteDebounceTime}
          setInputRef={setInputRef}
          handleKeypress={handleKeypress}
          onSearchBoxValueChange={onSearchBoxValueChange}
          identifier={identifier}
        />
      )}
      {helpText && (
        <InfoPopover
          class={{ adornment: !!searchConfig.adornmentConfig }}
          helpText={helpText}
          data-test-id={`${identifier}-popover`}
        />
      )}
      {hasValue(selectConfig) && (
        <FocusIndicator host={focusIndicatorHost} hasFocus={hasFocus} />
      )}
    </div>
  );
};
/**
 * SearchFieldProps - props for the input field
 */
interface SearchFieldProps {
  searchConfig: FluidFormElementConfig;
  searchTerm: string;
  deleteDebounceTime?: number;
  setInputRef: (el: HTMLFluidInputFieldElement) => void;
  handleKeypress: (keypress: KeyboardEvent) => void;
  onSearchBoxValueChange: (
    state: FluidFormElementState,
    selectRef: HTMLFluidSelectElement
  ) => void;
  identifier?: string;
}

/**
 * SearchField - embedded Input Field
 */
export const SearchField = (props: SearchFieldProps) => {
  const {
    searchConfig,
    searchTerm,
    deleteDebounceTime,
    setInputRef,
    handleKeypress,
    onSearchBoxValueChange,
    identifier,
  } = props;

  return (
    <fluid-input-field
      config={searchConfig}
      debounceDelete={deleteDebounceTime || 200}
      ref={setInputRef}
      value={searchTerm}
      onKeyDown={handleKeypress}
      onValueChange={(valueChangeEvent: CustomEvent<FluidFormElementState>) =>
        onSearchBoxValueChange(valueChangeEvent.detail, null)
      }
      _enableParts={true}
      data-test-id={`${identifier}-input-field`}
    />
  );
};

/**
 * SearchButton - optional embedded click-to-search button
 */
export const SearchButton = (props: {
  buttonConfig: FluidButtonConfig;
  onSearch: Function;
  theme: FluidTheme;
  dynamicSearch?: boolean;
  offsetLabel?: boolean;
  identifier?: string;
}) => {
  const {
    dynamicSearch,
    buttonConfig,
    onSearch,
    theme,
    offsetLabel,
    identifier,
  } = props;

  if (dynamicSearch) return;
  return (
    <div
      class={{
        'search-button': true,
        'offset-form-label': offsetLabel,
      }}
      data-test-id={`${identifier}-search-button-container`}>
      <fluid-button
        config={buttonConfig}
        onClick={() => onSearch()}
        theme={theme}
        overrideGlobalTheme={true}
        data-test-id={`${identifier}-search-button`}
        identifier={identifier}
      />
    </div>
  );
};
